import useIsMobile from '@/hooks/use-is-mobile';
import { Box, Flex, IconInfoCircle, Image, Popover, ProgressBar, Text } from '@cmpsr/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import getBadgeAndProgressBarVariant from '../get-badge-variant.util';
import { formatRiskDecimal } from '@/utils';

export default function RiskImpactProgressBar({
  impact,
  average,
  progressBarVariant,
}: {
  impact: number;
  average: number;
  progressBarVariant: string;
}) {
  const { ImpactBadgeComponent } = getBadgeAndProgressBarVariant(impact, average);

  const impactRelativePosition = (impact / (average * 2)) * 100;

  const [badgeWidth, setBadgeWidth] = useState<number | null>(60);
  const [avgWidth, setAvgWidth] = useState<number | null>(80);
  const [barWidth, setBarWidth] = useState<number | null>(600);

  const badgeRef = useCallback((node: HTMLDivElement) => {
    if (!node) setBadgeWidth(60);
    if (node !== null) setBadgeWidth(node.getBoundingClientRect().width);
  }, []);

  const avgRef = useCallback((node: HTMLDivElement) => {
    if (!node) setAvgWidth(80);
    if (node !== null) setAvgWidth(node.getBoundingClientRect().width);
  }, []);

  const barWidthRef = useRef<HTMLDivElement>(null);

  const handleResize = () => {
    if (!barWidthRef?.current) return;
    setBarWidth(barWidthRef.current.getBoundingClientRect().width);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
  }, []);

  useEffect(() => {
    handleResize();
  }, [barWidthRef.current]);

  const badgeMarginLeft = `max(
    min(
      calc(${impactRelativePosition}% - ${(badgeWidth ?? 60) / 2}px)
      , ${barWidth}px - ${(badgeWidth ?? 60) / 2}px - 25px)
    , 0%)`;
  const averageMarginLeft = `calc(50% - ${(avgWidth ?? 80) / 2}px)`;

  const isMobile = useIsMobile();

  return (
    <Box position="relative" ref={barWidthRef}>
      <ProgressBar size="xs" variant={progressBarVariant} value={impactRelativePosition} />
      <Flex
        position="absolute"
        zIndex="1"
        marginLeft={badgeMarginLeft}
        w="max-content"
        ref={badgeRef}
        transform="translateY(-50%)"
        gap="spacer-5"
      >
        <ImpactBadgeComponent impact={impact} />
      </Flex>
      <Flex
        direction="column"
        position="relative"
        marginLeft={averageMarginLeft}
        top="-15px"
        mx="auto"
        ref={avgRef}
        alignItems="center"
      >
        <Image
          display="block"
          src="/images/average-indicator-icon.svg"
          height="38px"
          width="2px"
          alt="progress bar thumb"
        />
        <Flex alignItems="baseline" position="relative">
          <Text variant="text-body-floating-label-regular" display="flex">
            {`Avg: ${formatRiskDecimal(average)}`}
          </Text>
          <Popover trigger={isMobile ? 'click' : 'hover'} closeOnBlur>
            <Popover.Trigger>
              <Box>
                <IconInfoCircle size="s" ml="5px" color="alert-inform-default" />
              </Box>
            </Popover.Trigger>
            <Popover.Content maxW="60vw">
              <Popover.Arrow backgroundColor="text-secondary" />
              <Popover.Body
                backgroundColor="text-secondary"
                borderColor="transparent"
                borderRadius="radii-1"
                p="spacer-2"
              >
                <Text variant="text-body-meta-regular" color="text-light">
                  This represents the average risk that someone your age and gender will develop this cancer over the
                  course of their lifetime.
                </Text>
              </Popover.Body>
            </Popover.Content>
          </Popover>
        </Flex>
      </Flex>
    </Box>
  );
}
